import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { products } from '@data/products';
import { getCartLineId, getProductVariant, resolveProductVariant } from '@utils/productVariants';
import { calculateCartTotals } from '@utils/money';
const cartStorageKey = 'aitechcare-cart';
const CartContext = createContext(undefined);
export const sanitizeCartState = (value) => {
    if (!value || typeof value !== 'object' || !Array.isArray(value.items)) {
        return { items: [] };
    }
    const items = value.items.flatMap((item) => {
        if (!item || typeof item !== 'object')
            return [];
        const product = products.find((candidate) => candidate.id === item.productId);
        const quantity = Number.isSafeInteger(item.quantity) && item.quantity > 0 ? item.quantity : 0;
        if (!product || !resolveProductVariant(product, item.variantId) || quantity === 0)
            return [];
        return [
            {
                productId: product.id,
                ...(item.variantId ? { variantId: item.variantId } : {}),
                quantity
            }
        ];
    });
    return { items };
};
export const cartReducer = (state, action) => {
    switch (action.type) {
        case 'add': {
            const product = products.find((item) => item.id === action.productId);
            if (!product || !resolveProductVariant(product, action.variantId))
                return state;
            const lineId = getCartLineId(action.productId, action.variantId);
            const existingItem = state.items.find((item) => getCartLineId(item.productId, item.variantId) === lineId);
            if (existingItem) {
                return {
                    items: state.items.map((item) => getCartLineId(item.productId, item.variantId) === lineId
                        ? { ...item, quantity: item.quantity + 1 }
                        : item)
                };
            }
            return {
                items: [
                    ...state.items,
                    {
                        productId: action.productId,
                        ...(action.variantId ? { variantId: action.variantId } : {}),
                        quantity: 1
                    }
                ]
            };
        }
        case 'remove':
            return {
                items: state.items.filter((item) => getCartLineId(item.productId, item.variantId) !== action.lineId)
            };
        case 'setQuantity':
            if (!Number.isSafeInteger(action.quantity))
                return state;
            if (action.quantity <= 0) {
                return {
                    items: state.items.filter((item) => getCartLineId(item.productId, item.variantId) !== action.lineId)
                };
            }
            return {
                items: state.items.map((item) => getCartLineId(item.productId, item.variantId) === action.lineId
                    ? { ...item, quantity: action.quantity }
                    : item)
            };
        case 'clear':
            return { items: [] };
        default:
            return state;
    }
};
const getInitialCartState = () => {
    try {
        const rawValue = localStorage.getItem(cartStorageKey);
        const parsedValue = rawValue ? JSON.parse(rawValue) : { items: [] };
        return sanitizeCartState(parsedValue);
    }
    catch {
        return { items: [] };
    }
};
export const CartProvider = ({ children }) => {
    const [state, dispatch] = useReducer(cartReducer, undefined, getInitialCartState);
    const [isOpen, setIsOpen] = useState(false);
    useEffect(() => {
        localStorage.setItem(cartStorageKey, JSON.stringify(state));
    }, [state]);
    const lines = useMemo(() => state.items.flatMap((item) => {
        const original = products.find((candidate) => candidate.id === item.productId);
        const product = original && resolveProductVariant(original, item.variantId);
        const quantity = Number.isSafeInteger(item.quantity) && item.quantity > 0 ? item.quantity : 0;
        return product && quantity > 0
            ? [
                {
                    product,
                    lineId: getCartLineId(item.productId, item.variantId),
                    variant: getProductVariant(product, item.variantId),
                    quantity,
                    totalMinor: product.priceMinor * quantity
                }
            ]
            : [];
    }), [state.items]);
    const value = useMemo(() => {
        const totals = calculateCartTotals(lines.map((line) => ({ priceMinor: line.product.priceMinor, quantity: line.quantity })));
        return {
            items: lines,
            totalItems: lines.reduce((sum, line) => sum + line.quantity, 0),
            ...totals,
            isOpen,
            addProduct: (productId, variantId) => {
                const product = products.find((candidate) => candidate.id === productId);
                if (!product || !resolveProductVariant(product, variantId))
                    return;
                dispatch({ type: 'add', productId, variantId });
                setIsOpen(true);
            },
            removeProduct: (lineId) => dispatch({ type: 'remove', lineId }),
            setQuantity: (lineId, quantity) => dispatch({ type: 'setQuantity', lineId, quantity }),
            clearCart: () => dispatch({ type: 'clear' }),
            openCart: () => setIsOpen(true),
            closeCart: () => setIsOpen(false),
            toggleCart: () => setIsOpen((current) => !current)
        };
    }, [isOpen, lines]);
    return _jsx(CartContext.Provider, { value: value, children: children });
};
export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartProvider');
    }
    return context;
};
